import { Injectable } from '@angular/core';
import { DateTime } from 'luxon';
import {
  Schedule_ItemType,
  ScheduleRule,
  ScheduleRuleRepeat,
  isRuleActiveOnDate,
  getActiveWeeklyRules,
  getEffectiveRuleTime,
  getLiveMealReferences,
} from '@cf-shared/data-model/schedule';
import { hoursToIsoTime } from '@cf-shared/time';
import { formatTime, weekdayName } from 'util/formatting';
import { SettingsService } from './settings.service';
import { LL } from 'i18n/i18n.service';
import { TranslatedString } from 'models/translated-string';

// ═══════════════════════════════════════════════════════════════════════════
// Types and Enums
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Edit scope for rescheduling operations.
 * Replaces RescheduleOption and ItemTimeEditOption.
 */
export enum ScheduleEditScope {
  ThisDayOnly = 'this-day-only',
  AllDays = 'all-days',
  ThisWeekday = 'this-weekday'
}

/**
 * Mode for rescheduling operations - determines how effective date is calculated.
 */
export enum RescheduleMode {
  /** Editing from a log entry (specific date context) */
  LogEntry = 'log-entry',
  /** Editing from Program Settings (weekly pattern context) */
  ScheduleEditing = 'schedule-editing'
}

/**
 * Parameters for rescheduling an item.
 * The service calculates the effective date internally based on mode and context.
 */
export interface RescheduleParams {
  /** The rules array to modify (mutated in place) */
  rules: ScheduleRule[];
  /** Type of schedule item */
  itemType: Schedule_ItemType;
  /** For meals: which meal (0, 1, 2...) */
  itemReference?: number;
  /** Edit scope */
  scope: ScheduleEditScope;
  /** New time in decimal hours */
  newTime_hr: number;

  // Context for calculating effective date
  /** Mode determines how effective date is calculated */
  mode: RescheduleMode;
  /** Today's date (ISO format) - always required */
  todayISO: string;
  /** For LogEntry mode: the date of the entry being rescheduled (ISO format) */
  entryDateISO?: string;
  /** For ThisDayOnly scope: the user-selected target date (ISO format) */
  selectedDateISO?: string;

  /** Current weekday (1-7, Monday-Sunday) */
  currentWeekday: number;
  /** Target weekday if moving (1-7, Monday-Sunday) */
  newWeekday?: number;
}

/**
 * Parameters for adding a one-time schedule item.
 */
export interface AddOneTimeItemParams {
  /** The rules array to modify (mutated in place) */
  rules: ScheduleRule[];
  /** Type of schedule item */
  itemType: Schedule_ItemType;
  /** Date for the item (ISO format) */
  dateISO: string;
  /** Time in decimal hours */
  time_hr: number;
  /** For meals: which meal (0, 1, 2...) */
  itemReference?: number;
  /**
   * Only for a workout being moved because the user skipped it: the local date
   * (YYYY-MM-DD) of the workout that was skipped. See `ScheduleRule` for why the
   * created rule carries this, and `findSkipRescheduledWorkoutRule` for who reads it.
   */
  skipRescheduledFromDateISO?: string;
}

/**
 * Unified display info for schedule items (workouts, meals, etc.).
 * Contains all data needed for both display and edit operations.
 */
export interface ScheduleItemDisplayInfo {
  // Display data
  /** Primary label: "Monday" for workouts, "Meal 1" for meals */
  label: TranslatedString;
  /** Formatted time "10:00 AM" */
  time: string;

  // Data needed for edit operations (so UI doesn't interpret rule directly)
  /** Type of schedule item */
  itemType: Schedule_ItemType;
  /** For meals: which meal (0, 1, 2...) */
  itemReference?: number;
  /** 1-7 for weekly rules (Monday-Sunday) */
  weekday?: number;
  /** Decimal hours for edit operations */
  time_hr: number;

  /** @internal - Used by service methods, UI should not access directly */
  _rule: ScheduleRule;
}

/**
 * Day option for reschedule dropdown.
 */
export interface DayOption {
  /** Weekday 1-7 (Monday-Sunday) */
  value: number;
  /** For ThisDayOnly mode: specific date ISO */
  dateISO?: string;
  /** Display label */
  label: TranslatedString;
  /** Whether this day already has a workout */
  hasWorkout: boolean;
  /** Whether this option is disabled */
  disabled: boolean;
  /** Whether this is the current day being rescheduled */
  isCurrent: boolean;
}

/**
 * Parameters for redistributing meal times after one meal is moved past others.
 */
export interface RedistributeMealTimesParams {
  /** The rules array to modify (mutated in place) */
  rules: ScheduleRule[];
  /** Every meal's current time in decimal hours, indexed by meal reference */
  allMealTimes_hr: number[];
  /** Which meal is being moved (0, 1, 2...) */
  sourceRef: number;
  /** The moved meal's new time in decimal hours */
  targetTime_hr: number;
  /** Edit scope */
  scope: ScheduleEditScope;
  /** Today's date (ISO format) */
  todayISO: string;
  /** The date of the log entry being rescheduled (ISO format) */
  entryDateISO?: string;
  /** Current weekday (1-7, Monday-Sunday) */
  currentWeekday: number;
}

/**
 * Parameters for shifting meals after a DayStart or DayEnd change.
 */
export interface ShiftMealsForDayBoundaryChangeParams {
  /** The rules array to modify (mutated in place) */
  rules: ScheduleRule[];
  /** Which boundary moved */
  changedBoundary: Schedule_ItemType.DayStart | Schedule_ItemType.DayEnd;
  /** The boundary's time before the change, in decimal hours */
  oldTime_hr: number;
  /** The boundary's time after the change, in decimal hours */
  newTime_hr: number;
  /** Edit scope */
  scope: ScheduleEditScope;
  /** Mode determines how effective date is calculated */
  mode: RescheduleMode;
  /** Today's date (ISO format) */
  todayISO: string;
  /** For LogEntry mode: the date of the entry being rescheduled (ISO format) */
  entryDateISO?: string;
  /** For ThisDayOnly scope: the user-selected target date (ISO format) */
  selectedDateISO?: string;
  /** Current weekday (1-7, Monday-Sunday) */
  currentWeekday: number;
}

/**
 * Parameters for getting weekday options for the reschedule UI.
 * Used for ScheduleEditing mode to get abstract weekday options.
 */
export interface GetWeekdayOptionsParams {
  rules: ScheduleRule[];
  todayISO: string;
  currentWeekday: number;
}

/**
 * What the private `apply*Reschedule` helpers work from, once `rescheduleItem` has
 * worked out the effective date and whether the workout is moving weekday.
 *
 * Not exported: nothing outside this service builds one. It is a parameter object
 * rather than a list of positional arguments so that a helper which does not need a
 * field simply does not read it — `applyThisDayOnlyReschedule` needs neither weekday
 * nor the moving flag, and used to have to name them anyway.
 */
interface ApplyRescheduleParams {
  /** The rules array to modify (mutated in place) */
  rules: ScheduleRule[];
  /** Type of schedule item */
  itemType: Schedule_ItemType;
  /** For meals: which meal (0, 1, 2...) */
  itemReference?: number;
  /** New time in decimal hours */
  newTime_hr: number;
  /** The date from which the change takes effect (ISO format) */
  dateISO: string;
  /** Current weekday (1-7, Monday-Sunday) */
  currentWeekday: number;
  /** Target weekday if moving (1-7, Monday-Sunday) */
  newWeekday?: number;
  /** Whether this is a workout being moved to a different weekday */
  isMovingToNewWeekday: boolean;
}

/** Minimum gap, in hours, between DayStart and the first meal. */
const MIN_GAP_FIRST_MEAL_HR = 1;
/** Minimum gap, in hours, between two consecutive meals. */
const MIN_GAP_BETWEEN_MEALS_HR = 1;
/** Minimum gap, in hours, between the last meal and DayEnd. */
const MIN_GAP_LAST_MEAL_HR = 0.5;

// ═══════════════════════════════════════════════════════════════════════════
// Service
// ═══════════════════════════════════════════════════════════════════════════

@Injectable({ providedIn: 'root' })
export class ScheduleRuleService {

  constructor(private settings: SettingsService) {}

  // ═══════════════════════════════════════════════════════════════════════════
  // RULE MANIPULATION - Core business logic
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Reschedule an item with the specified edit scope.
   * Handles all the complex rule manipulation logic.
   * Mutates the rules array in place.
   *
   * The effective date is calculated internally based on mode, scope, and context.
   */
  rescheduleItem(params: RescheduleParams): void {
    const { rules, itemType, itemReference, scope, newTime_hr, currentWeekday, newWeekday } = params;
    const isMovingToNewWeekday = itemType === Schedule_ItemType.Workout &&
      newWeekday !== undefined &&
      newWeekday !== currentWeekday;

    // calculate effective date based on mode and scope
    const dateISO = this.calculateEffectiveDate(params, isMovingToNewWeekday);

    const applyParams: ApplyRescheduleParams = {
      rules, itemType, itemReference, newTime_hr, dateISO, currentWeekday, newWeekday, isMovingToNewWeekday,
    };

    switch (scope) {
      case ScheduleEditScope.AllDays:
      case ScheduleEditScope.ThisWeekday:
        // both scopes rewrite the repeating weekly pattern, so they share one walk
        this.applyRecurringPatternReschedule(applyParams, scope);
        break;
      case ScheduleEditScope.ThisDayOnly:
        this.applyThisDayOnlyReschedule(applyParams);
        break;
    }
  }

  /**
   * Calculate the effective date for a reschedule operation.
   * This encapsulates the business logic for when rule changes should take effect.
   */
  private calculateEffectiveDate(params: RescheduleParams, _isMovingToNewWeekday: boolean): string {
    const { mode, scope, todayISO, entryDateISO, selectedDateISO } = params;

    // ThisDayOnly scope: use the selected/entry date (targeting a specific day)
    if (scope === ScheduleEditScope.ThisDayOnly) {
      return selectedDateISO || entryDateISO || todayISO;
    }

    // ThisWeekday and AllDays scopes: always use today
    // These change the weekly pattern, which should take effect immediately
    if (scope === ScheduleEditScope.ThisWeekday || scope === ScheduleEditScope.AllDays) {
      return todayISO;
    }

    // Fallback for any other cases
    if (mode === RescheduleMode.ScheduleEditing) {
      return todayISO;
    }
    return entryDateISO || todayISO;
  }

  /**
   * Add a one-time schedule item for a specific date.
   * Mutates the rules array in place.
   */
  addOneTimeItem(params: AddOneTimeItemParams): void {
    const { rules, itemType, dateISO, time_hr, itemReference, skipRescheduledFromDateISO } = params;
    rules.push({
      itemType,
      repeat: ScheduleRuleRepeat.Never,
      dateISO,
      time_hr,
      itemReference,
      skipRescheduledFromDateISO,
    });
  }

  /**
   * Find the one-time workout rule that a specific skip created when it moved that
   * workout to another day, or undefined when there is none.
   *
   * This is what undoing a skip must delete, and the ONLY thing it may delete. Every
   * condition below rules out a rule some other part of the app created:
   *
   *   - a repeating rule is never what a skip made, and deleting one would destroy a
   *     recurring training day rather than a single session;
   *   - an exclusion rule on that date says the user moved a workout AWAY from it,
   *     so deleting it would bring back a workout they got rid of on purpose;
   *   - a rule with no receipt, or with one naming a different skipped day, belongs
   *     to somebody else — most often a workout the user added for that day by hand.
   *
   * @param rules the schedule's rules
   * @param dateISO the day the workout was moved TO
   * @param skippedFromDateISO the local date of the workout that was skipped
   */
  findSkipRescheduledWorkoutRule(
    rules: ScheduleRule[],
    dateISO: string,
    skippedFromDateISO: string
  ): ScheduleRule | undefined {
    return rules.find(rule =>
      rule.itemType === Schedule_ItemType.Workout &&
      rule.repeat === ScheduleRuleRepeat.Never &&
      !rule.exclude &&
      rule.dateISO === dateISO &&
      rule.skipRescheduledFromDateISO === skippedFromDateISO
    );
  }

  /**
   * Add an exclusion rule to skip an item on a specific date.
   * Mutates the rules array in place.
   */
  addExclusionRule(rules: ScheduleRule[], dateISO: string, itemType: Schedule_ItemType): void {
    rules.push({
      itemType,
      repeat: ScheduleRuleRepeat.Never,
      dateISO,
      exclude: true,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DISPLAY HELPERS - For UI components
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Get active training days with formatted display info.
   */
  getActiveTrainingDays(rules: ScheduleRule[], todayISO: string): ScheduleItemDisplayInfo[] {
    // get the newest active workout rule per weekday (getActiveWeeklyRules returns sorted oldest-first)
    let workoutRules: ScheduleRule[] = [];
    for (let weekday = 1; weekday <= 7; ++weekday) {
      const weekdayRules = getActiveWeeklyRules(rules, Schedule_ItemType.Workout, weekday, todayISO);
      if (weekdayRules.length > 0) {
        workoutRules.push(weekdayRules[weekdayRules.length - 1]);
      }
    }

    // sort by display order (respects user's first day of week preference)
    const displayOrder = this.settings.getWeekDayDisplayOrder();
    workoutRules = workoutRules.sort((a, b) => {
      // convert Luxon weekday (1-7) to ISO index (0-6): Luxon 1-6 → ISO 0-5, Luxon 7 → ISO 6
      const aIsoIdx = a.weekday === 7 ? 6 : a.weekday - 1;
      const bIsoIdx = b.weekday === 7 ? 6 : b.weekday - 1;
      return displayOrder.indexOf(aIsoIdx) - displayOrder.indexOf(bIsoIdx);
    });

    return workoutRules.map(rule => ({
      label: weekdayName(rule.weekday),
      time: formatTime(DateTime.fromISO(hoursToIsoTime(rule.time_hr)), this.settings),
      itemType: Schedule_ItemType.Workout,
      itemReference: undefined,
      weekday: rule.weekday,
      time_hr: rule.time_hr,
      _rule: rule,
    }));
  }

  /**
   * Get active meal times with formatted display info.
   */
  getActiveMealTimes(rules: ScheduleRule[], todayISO: string): ScheduleItemDisplayInfo[] {
    let mealRules = rules.filter(
      rule => rule.itemType === Schedule_ItemType.Meal &&
              rule.repeat === ScheduleRuleRepeat.Daily &&
              isRuleActiveOnDate(rule, todayISO)
    );

    // sort by time
    mealRules = mealRules.sort((a, b) => a.time_hr - b.time_hr);

    return mealRules.map(rule => {
      const mealNumber = (rule.itemReference ?? 0) + 1;
      return {
        label: LL.dayItems.titles.meal({ number: mealNumber }),
        time: formatTime(DateTime.fromISO(hoursToIsoTime(rule.time_hr)), this.settings),
        itemType: Schedule_ItemType.Meal,
        itemReference: rule.itemReference,
        weekday: rule.weekday,
        time_hr: rule.time_hr,
        _rule: rule,
      };
    });
  }

  /**
   * Get weekday options for the reschedule dropdown (ScheduleEditing mode).
   * Returns abstract weekday options based on weekly schedule rules.
   */
  getWeekdayOptions(params: GetWeekdayOptionsParams): DayOption[] {
    const { rules, todayISO, currentWeekday } = params;

    // get configured workout weekdays from schedule rules
    const workoutWeekdays = new Set<number>();
    const activeTrainingDays = this.getActiveTrainingDays(rules, todayISO);
    for (const day of activeTrainingDays) {
      if (day.weekday) {
        workoutWeekdays.add(day.weekday);
      }
    }

    // show weekdays in locale order
    const displayOrder = this.settings.getWeekDayDisplayOrder();
    const allWeekdays = displayOrder.map(isoIdx => isoIdx === 6 ? 7 : isoIdx + 1);

    return allWeekdays.map(weekday => {
      const hasWorkout = workoutWeekdays.has(weekday);
      const isCurrent = weekday === currentWeekday;

      return {
        value: weekday,
        label: weekdayName(weekday),
        hasWorkout,
        // disable if has workout, unless it's the current weekday being rescheduled
        disabled: hasWorkout && !isCurrent,
        isCurrent,
      };
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // PRIVATE HELPERS
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Rewrite the REPEATING pattern for an item, taking effect from `dateISO` onward.
   *
   * One walk serves both of the edit scopes that change a repeating pattern:
   *
   *   - `AllDays` — "change this from now on", every day the item happens on;
   *   - `ThisWeekday` — "change only this one weekday", leaving the other days alone.
   *
   * The cascade this rests on: a rule that is ALREADY LIVE is ended (`endDateISO` is
   * set) and replaced, never edited, because editing it would change what the user's
   * schedule said in the past. Only a rule that has not started yet is edited in place.
   *
   * The two scopes were written out separately until 2026-09-17, and the two copies
   * had drifted apart in small ways that nothing explained. They are one body now,
   * and every way they still differ is a decision marked `SCOPE DIFFERENCE` below.
   * Nothing else differs, so a change to the cascade is made once instead of twice.
   */
  private applyRecurringPatternReschedule(
    params: ApplyRescheduleParams,
    scope: ScheduleEditScope.AllDays | ScheduleEditScope.ThisWeekday
  ): void {
    const { rules, itemType, itemReference, newTime_hr, dateISO, currentWeekday, newWeekday, isMovingToNewWeekday } = params;

    const targetWeekday = isMovingToNewWeekday ? newWeekday : currentWeekday;
    const previousWeekday = isMovingToNewWeekday ? currentWeekday : undefined;
    const isWeekdayScoped = scope === ScheduleEditScope.ThisWeekday;

    if (itemType === Schedule_ItemType.Workout) {
      // workouts repeat weekly, so the pattern to rewrite is the set of weekly rules.
      // capture the length first: replacements are appended and must not be re-walked
      const ruleCount = rules.length;
      let createdReplacement = false;

      for (let iRule = 0; iRule < ruleCount; iRule++) {
        const rule = rules[iRule];
        if (rule.itemType !== Schedule_ItemType.Workout) continue;

        if (rule.repeat === ScheduleRuleRepeat.Weekly) {
          // SCOPE DIFFERENCE 1 — which weekly rules this edit may touch at all.
          // "this weekday" may touch only the weekday being edited, plus the weekday
          // the workout is moving away from when it is moving. "all days" has no
          // weekday scope and touches every weekly workout rule there is.
          if (isWeekdayScoped && rule.weekday !== targetWeekday && rule.weekday !== previousWeekday) continue;

          const isRuleOnPreviousWeekday = previousWeekday !== undefined && rule.weekday === previousWeekday;
          const isRuleOnTargetWeekday = rule.weekday === targetWeekday;
          const isRuleActiveToday = isRuleActiveOnDate(rule, dateISO);

          if (isRuleActiveToday && rule.dateISO < dateISO) {
            // rule is already live and started earlier - end it and replace it
            rule.endDateISO = dateISO;

            // a rule already sitting on the weekday the workout is moving INTO is
            // being displaced by that workout, so nothing replaces it
            const isDisplacedByTheMovedWorkout = isRuleOnTargetWeekday && isMovingToNewWeekday;

            // SCOPE DIFFERENCE 2 — how many replacements get written. "this weekday"
            // writes at most ONE, because everything it touched collapses onto the one
            // target weekday and a second rule there would be a duplicate training day.
            // "all days" writes one per weekday, because each weekday keeps its own rule.
            const alreadyWroteTheOneAllowedReplacement = isWeekdayScoped && createdReplacement;

            if (!isDisplacedByTheMovedWorkout && !alreadyWroteTheOneAllowedReplacement) {
              rules.push({
                itemType,
                repeat: ScheduleRuleRepeat.Weekly,
                time_hr: newTime_hr,
                // SCOPE DIFFERENCE 3 — which weekday the replacement lands on.
                // "this weekday" collapses everything onto the target weekday. Under
                // "all days" only a rule on the weekday being moved away from lands on
                // the target weekday; every other rule keeps its own weekday.
                weekday: isWeekdayScoped || isRuleOnPreviousWeekday ? targetWeekday : rule.weekday,
                dateISO: dateISO,
              });
              createdReplacement = true;
            }
          } else if (dateISO <= rule.dateISO) {
            // rule has not started yet - safe to edit in place, no past day changes
            if (isRuleOnPreviousWeekday) {
              // this is the workout being moved: carry it to its new weekday
              rule.weekday = targetWeekday;
              rule.time_hr = newTime_hr;
              createdReplacement = true;
            } else if (isRuleOnTargetWeekday && isMovingToNewWeekday) {
              // another workout is arriving on this weekday, so this one ends
              rule.endDateISO = dateISO;
            } else {
              rule.time_hr = newTime_hr;
            }
          }
        } else if (rule.repeat === ScheduleRuleRepeat.Never) {
          // SCOPE DIFFERENCE 4 — which one-off workouts already on the calendar get
          // the new time. "all days" retimes every one still to come, which is what
          // "from now on" reads as when no weekday limits it. "this weekday" retimes
          // every one still to come whose own date falls on the edited weekday: "all
          // Mondays" means all of them, including a Monday the user once set by hand
          // (owner decision on df-34a04448924e — to keep one Monday different, the
          // user sets it again afterwards). The one on the effective date is always
          // retimed, as before.
          // when the workout is also MOVING to another weekday, only the one on the
          // effective date is retimed: a future one-off on the old weekday stays on
          // that weekday, and giving it the moved workout's time would be surprising.
          const isLaterOneOffOnEditedWeekday = !isMovingToNewWeekday &&
            rule.dateISO > dateISO &&
            DateTime.fromISO(rule.dateISO).weekday === targetWeekday;
          const isOneTimeRuleInScope = isWeekdayScoped
            ? rule.dateISO === dateISO || isLaterOneOffOnEditedWeekday
            : rule.dateISO >= dateISO;
          if (isOneTimeRuleInScope) {
            rule.time_hr = newTime_hr;
          }
        }
      }
    } else {
      // meals, day start and day end have no weekday of their own, so their pattern is
      // a daily rule - which "this weekday" overrides rather than replaces, below
      for (const rule of rules) {
        // a "this day only" time on a day the new weekly rule covers: from the effective
        // date on, and on the edited weekday itself. A one-off has no weekday field, so
        // its own date decides. Exclusions are left alone.
        const isOneOffOnEditedWeekday = rule.repeat === ScheduleRuleRepeat.Never &&
          !rule.exclude &&
          rule.dateISO >= dateISO &&
          DateTime.fromISO(rule.dateISO).weekday === targetWeekday;
        if (itemType === rule.itemType &&
            (rule.itemReference === undefined || rule.itemReference === itemReference) &&
            !rule.endDateISO &&
            // SCOPE DIFFERENCE 5a — which existing rules get ended. "all days" ends
            // every live rule for this item. "this weekday" ends a rule already tied to
            // this weekday, and a one-off dated on that weekday from today on: "every
            // Monday" means every Monday, including one set by hand (the same answer
            // the workout branch gives, and "all days" gives for meals). A DAILY rule
            // has no weekday at all, so this test is false for it and the daily rule is
            // deliberately left alive. That is what makes the new rule below an
            // override for one weekday rather than a replacement of the whole pattern.
            // Do not "fix" it into matching.
            (!isWeekdayScoped || rule.weekday === targetWeekday || isOneOffOnEditedWeekday)) {
          rule.endDateISO = dateISO;
        }
      }

      const replacement: ScheduleRule = {
        itemType,
        itemReference: itemType === Schedule_ItemType.Meal ? itemReference : undefined,
        // SCOPE DIFFERENCE 5b — what the new rule repeats as. "all days" writes a
        // daily rule, which is the pattern itself. "this weekday" writes a weekly one,
        // which sits above the daily rule in the cascade and so applies on that one
        // weekday only.
        repeat: isWeekdayScoped ? ScheduleRuleRepeat.Weekly : ScheduleRuleRepeat.Daily,
        time_hr: newTime_hr,
        dateISO: dateISO,
      };
      // only a weekly rule carries a weekday; a daily one leaves the field unset
      if (isWeekdayScoped) {
        replacement.weekday = targetWeekday;
      }
      rules.push(replacement);
    }
  }

  private applyThisDayOnlyReschedule(params: ApplyRescheduleParams): void {
    const { rules, itemType, itemReference, newTime_hr, dateISO } = params;
    // Filter out any one-time rules for the same item on the target date
    const filteredRules = rules.filter(rule => {
      if (rule.itemType !== itemType ||
          rule.repeat === ScheduleRuleRepeat.Daily ||
          rule.repeat === ScheduleRuleRepeat.Weekly ||
          rule.dateISO !== dateISO) {
        return true;
      }

      if (rule.itemType === Schedule_ItemType.Meal && rule.itemReference !== itemReference) {
        return true;
      }

      return false;
    });

    // Replace the rules array contents
    rules.length = 0;
    rules.push(...filteredRules);

    // Add the new one-time rule
    rules.push({
      itemType,
      itemReference: itemType === Schedule_ItemType.Meal ? itemReference : undefined,
      repeat: ScheduleRuleRepeat.Never,
      dateISO: dateISO,
      time_hr: newTime_hr,
    });

    // Note: Workout move exclusion is handled separately by the caller via addWorkoutMoveExclusion()
  }

  /**
   * Add an exclusion rule for a workout being moved from its original date.
   * Call this separately when moving a workout in ThisDayOnly mode.
   */
  addWorkoutMoveExclusion(rules: ScheduleRule[], originalDateISO: string): void {
    // Remove any existing one-time rule for the original date
    const existingRule = rules.find(
      rule => rule.itemType === Schedule_ItemType.Workout &&
              rule.repeat === ScheduleRuleRepeat.Never &&
              rule.dateISO === originalDateISO
    );
    if (existingRule) {
      const index = rules.indexOf(existingRule);
      rules.splice(index, 1);
    }

    // Add exclusion rule
    rules.push({
      itemType: Schedule_ItemType.Workout,
      repeat: ScheduleRuleRepeat.Never,
      dateISO: originalDateISO,
      exclude: true,
    });
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MULTI-ITEM RULES - rewrite several items so the day stays consistent
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Redistribute meal times to maintain chronological order when a meal is rescheduled past other meals.
   * Mutates the rules array in place.
   * Returns true if redistribution was applied (meal crossed other meals), false otherwise.
   */
  redistributeMealTimes(params: RedistributeMealTimesParams): boolean {
    const { rules, allMealTimes_hr, sourceRef, targetTime_hr, scope, todayISO, entryDateISO, currentWeekday } = params;

    // if the target time doesn't cross another meal, no redistribution needed
    const sourceTime = allMealTimes_hr[sourceRef];
    const isForward = targetTime_hr > sourceTime;
    const crossesOtherMeal = isForward
      ? allMealTimes_hr.some((t, i) => i !== sourceRef && t > sourceTime && t <= targetTime_hr)
      : allMealTimes_hr.some((t, i) => i !== sourceRef && t < sourceTime && t >= targetTime_hr);
    if (!crossesOtherMeal) return false;

    // remove source time, insert target time in sorted position
    const newTimes = [...allMealTimes_hr];
    newTimes.splice(sourceRef, 1);
    let insertIdx = newTimes.findIndex(t => t > targetTime_hr);
    if (insertIdx === -1) insertIdx = newTimes.length;
    newTimes.splice(insertIdx, 0, targetTime_hr);

    // apply reschedule for each meal whose time changed
    for (let i = 0; i < newTimes.length; i++) {
      if (newTimes[i] !== allMealTimes_hr[i]) {
        this.rescheduleItem({
          rules,
          itemType: Schedule_ItemType.Meal,
          itemReference: i,
          scope,
          newTime_hr: newTimes[i],
          mode: RescheduleMode.LogEntry,
          todayISO,
          entryDateISO,
          currentWeekday,
        });
      }
    }

    return true;
  }

  /**
   * Shift meals that would violate minimum gap constraints after a DayStart/DayEnd change.
   * Call it after the DayStart/DayEnd rule itself was already rewritten. Mutates the rules array in place.
   * Forward-cascades from first meal when DayStart moves later,
   * backward-cascades from last meal when DayEnd moves earlier.
   * Falls back to linear spread if cascade overflows the opposite boundary.
   */
  shiftMealsForDayBoundaryChange(params: ShiftMealsForDayBoundaryChangeParams): void {
    const { rules, changedBoundary, oldTime_hr, newTime_hr, scope, mode, todayISO, entryDateISO, selectedDateISO, currentWeekday } = params;

    // resolve the effective date for rule lookups
    const effectiveDateISO = scope === ScheduleEditScope.ThisDayOnly
      ? (selectedDateISO ?? entryDateISO ?? todayISO)
      : todayISO;

    // only the meal slots still in force: a slot the user dropped by lowering their meals
    // per day keeps its ended rules, has no time, and would otherwise stop the whole shift
    const mealReferences = getLiveMealReferences(rules, effectiveDateISO);
    if (mealReferences.length === 0) return;

    // get the other boundary's time
    const otherBoundaryType = changedBoundary === Schedule_ItemType.DayStart
      ? Schedule_ItemType.DayEnd : Schedule_ItemType.DayStart;
    const otherBoundaryTime = getEffectiveRuleTime(rules, otherBoundaryType, effectiveDateISO, currentWeekday);
    if (otherBoundaryTime == null) return;

    const dayStartTime = changedBoundary === Schedule_ItemType.DayStart ? newTime_hr : otherBoundaryTime;
    const dayEndTime = changedBoundary === Schedule_ItemType.DayEnd ? newTime_hr : otherBoundaryTime;

    // get current meal times (after the DayStart/DayEnd rule was already applied)
    const mealTimes: number[] = [];
    for (const reference of mealReferences) {
      const t = getEffectiveRuleTime(rules, Schedule_ItemType.Meal, effectiveDateISO, currentWeekday, reference);
      if (t != null) mealTimes.push(t);
      else return; // if any meal time is missing, skip
    }

    // only shift meals when boundaries move inward (DayStart later or DayEnd earlier)
    // if boundaries move outward, gaps can only increase — no shifts needed
    const newMealTimes = [...mealTimes];
    let overflowed = false;

    // forward-cascade: if DayStart moved later
    if (changedBoundary === Schedule_ItemType.DayStart && newTime_hr > oldTime_hr) {
      for (let i = 0; i < newMealTimes.length; i++) {
        const minTime = i === 0 ? dayStartTime + MIN_GAP_FIRST_MEAL_HR : newMealTimes[i - 1] + MIN_GAP_BETWEEN_MEALS_HR;
        if (newMealTimes[i] < minTime) {
          newMealTimes[i] = minTime;
        }
      }
      // check if last meal overflows past DayEnd
      if (newMealTimes[newMealTimes.length - 1] > dayEndTime - MIN_GAP_LAST_MEAL_HR) {
        overflowed = true;
      }
    }

    // backward-cascade: if DayEnd moved earlier
    if (changedBoundary === Schedule_ItemType.DayEnd && newTime_hr < oldTime_hr) {
      for (let i = newMealTimes.length - 1; i >= 0; i--) {
        const maxTime = i === newMealTimes.length - 1 ? dayEndTime - MIN_GAP_LAST_MEAL_HR : newMealTimes[i + 1] - MIN_GAP_BETWEEN_MEALS_HR;
        if (newMealTimes[i] > maxTime) {
          newMealTimes[i] = maxTime;
        }
      }
      // check if first meal overflows before DayStart
      if (newMealTimes[0] < dayStartTime + MIN_GAP_FIRST_MEAL_HR) {
        overflowed = true;
      }
    }

    // if cascade overflowed, fall back to linear spread
    if (overflowed) {
      const firstTime = dayStartTime + MIN_GAP_FIRST_MEAL_HR;
      const lastTime = dayEndTime - MIN_GAP_LAST_MEAL_HR;
      for (let i = 0; i < newMealTimes.length; i++) {
        if (newMealTimes.length === 1) {
          newMealTimes[i] = firstTime;
        } else {
          newMealTimes[i] = firstTime + (lastTime - firstTime) * i / (newMealTimes.length - 1);
        }
      }
    }

    // round to 15-minute increments, then re-enforce gaps (rounding can shrink them)
    for (let i = 0; i < newMealTimes.length; i++) {
      newMealTimes[i] = Math.round(newMealTimes[i] * 4) / 4;
    }
    for (let i = 1; i < newMealTimes.length; i++) {
      if (newMealTimes[i] - newMealTimes[i - 1] < MIN_GAP_BETWEEN_MEALS_HR) {
        newMealTimes[i] = Math.ceil((newMealTimes[i - 1] + MIN_GAP_BETWEEN_MEALS_HR) * 4) / 4;
      }
    }

    // apply reschedule for each meal whose time actually changed
    for (let i = 0; i < newMealTimes.length; i++) {
      if (newMealTimes[i] !== mealTimes[i]) {
        this.rescheduleItem({
          rules,
          itemType: Schedule_ItemType.Meal,
          itemReference: mealReferences[i],
          scope,
          newTime_hr: newMealTimes[i],
          mode,
          todayISO,
          entryDateISO,
          selectedDateISO,
          currentWeekday,
        });
      }
    }
  }

  /**
   * Which weekdays have an active weekly workout rule on `todayISO`.
   * Index 0 is Monday, index 6 is Sunday — the same layout as `DeclaredState.trainingDays`.
   */
  getTrainingDaysFromRules(rules: ScheduleRule[], todayISO: string): boolean[] {
    const trainingDays: boolean[] = [];
    for (let iWeekday = 1; iWeekday <= 7; iWeekday++) {
      const weeklyRule = rules.find(
        rule => rule.itemType === Schedule_ItemType.Workout &&
                rule.repeat === ScheduleRuleRepeat.Weekly &&
                rule.weekday === iWeekday &&
                isRuleActiveOnDate(rule, todayISO)
      );
      trainingDays.push(weeklyRule !== undefined);
    }
    return trainingDays;
  }
}
